import React from 'react';
import { FONT_MONO, FONT_SERIF } from '../../views/landing/landingData';

/**
 * Shared primitives for the studio port of the handoff design ("HyVay Studio"):
 * tooltips, icon buttons, the preview skeleton, empty states and small
 * formatters. Everything is inline-styled against the landing tokens (`--hv-*`)
 * so the studio, landing and account pages share one look and one theme switch.
 */

export const THEME_KEY = 'hyvay:landing-theme';
export type Theme = 'light' | 'dark';

export function readTheme(): Theme {
    try {
        const v = localStorage.getItem(THEME_KEY);
        if (v === 'dark' || v === 'light') return v;
    } catch { /* storage unavailable */ }
    return 'light';
}

export function writeTheme(t: Theme): void {
    try { localStorage.setItem(THEME_KEY, t); } catch { /* ignore */ }
}

/** Publishing a site costs this many credits (mirrors PUBLISH_COST in core-api). */
export const PUBLISH_COST = 10;

/** Generic follow-up chips under the last assistant reply. Static for now; the
 *  backend can supply per-project suggestions later without touching the UI. */
export const SUGGESTIONS = [
    'Add a contact section',
    'Warmer color palette',
    'Make it mobile-friendly',
    'Add an FAQ section',
];

export function fmtCredits(n: number | undefined): string {
    if (typeof n !== 'number' || Number.isNaN(n)) return '—';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** "0.4s", "12s", "1m 04s" — the per-step timing shown in the chat card. */
export function fmtDuration(ms: number): string {
    if (ms < 0) ms = 0;
    if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return `${m}m ${String(s).padStart(2, '0')}s`;
}

/* ------------------------------------------------------------------ tooltip */

const bubbleBase: React.CSSProperties = {
    position: 'absolute', top: 'calc(100% + 8px)', whiteSpace: 'nowrap', padding: '5px 9px', borderRadius: 7,
    background: '#24262A', color: '#F3F2EC', fontSize: 11.5, fontWeight: 600, opacity: 0, pointerEvents: 'none',
    transition: 'opacity 0.15s ease', zIndex: 12,
};

/** The design's hover tooltip: `.hv-tt:hover .hv-tt-bubble { opacity: 1 }`. */
export const Tip: React.FC<{ label: string; align?: 'left' | 'center' | 'right'; style?: React.CSSProperties; children: React.ReactNode }> = ({ label, align = 'center', style, children }) => (
    <div className="hv-tt" style={{ position: 'relative', display: 'flex', flex: 'none', ...style }}>
        {children}
        <span
            className="hv-tt-bubble"
            style={{
                ...bubbleBase,
                ...(align === 'left' ? { left: 0 } : align === 'right' ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' }),
            }}
        >
            {label}
        </span>
    </div>
);

/* ------------------------------------------------------------------ buttons */

/** 34px round outlined icon button from the top bar. */
export const RoundBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { size?: number; filled?: boolean }> = ({ size = 34, filled, style, className, children, ...rest }) => (
    <button
        type="button"
        className={`hv-h-mint-bd ${className ?? ''}`}
        style={{
            width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--hv-line)',
            borderRadius: 999, background: filled ? 'var(--hv-card)' : 'transparent', color: 'var(--hv-body)', cursor: 'pointer', padding: 0,
            ...style,
        }}
        {...rest}
    >
        {children}
    </button>
);

/** Text + icon pill from the top bar (Export, Help). */
export const PillBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { outlined?: boolean }> = ({ outlined = true, style, className, children, ...rest }) => (
    <button
        type="button"
        className={`${outlined ? 'hv-h-mint-bd' : 'hv-h-mint'} ${className ?? ''}`}
        style={{
            display: 'flex', alignItems: 'center', gap: 7, height: 34, padding: '0 13px', border: outlined ? '1px solid var(--hv-line)' : 0,
            borderRadius: 999, background: 'transparent', color: 'var(--hv-body)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            ...style,
        }}
        {...rest}
    >
        {children}
    </button>
);

/** Solid brand button (Publish, Confirm, View site). */
export const BrandBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ style, className, children, disabled, ...rest }) => (
    <button
        type="button"
        disabled={disabled}
        className={`${disabled ? '' : 'hv-h-brand-dark'} ${className ?? ''}`}
        style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, padding: '0 18px', border: 0, borderRadius: 999,
            background: disabled ? 'color-mix(in srgb,var(--brand,#13422C) 55%,var(--hv-page-2))' : 'var(--brand,#13422C)',
            color: '#FFFDF7', fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
            ...style,
        }}
        {...rest}
    >
        {children}
    </button>
);

/** Ghost button paired with BrandBtn in dialogs (Cancel, Close). */
export const GhostBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ style, className, children, ...rest }) => (
    <button
        type="button"
        className={`hv-h-card2-ink ${className ?? ''}`}
        style={{
            flex: 1, height: 48, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', fontSize: 13.5, fontWeight: 700,
            color: 'var(--hv-body)', cursor: 'pointer', ...style,
        }}
        {...rest}
    >
        {children}
    </button>
);

/* ---------------------------------------------------------------- overlays */

/** Modal scrim + card from the design (Publish, Top up). */
export const Modal: React.FC<{ open: boolean; onClose: () => void; maxWidth?: number; label: string; children: React.ReactNode }> = ({ open, onClose, maxWidth = 480, label, children }) => {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);
    if (!open) return null;
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(20,21,22,0.6)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={label}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', maxWidth, maxHeight: '90vh', overflow: 'auto', borderRadius: 26, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 60px 90px -40px rgba(0,0,0,0.5)', animation: 'hvRise 0.4s cubic-bezier(0.2,0.9,0.24,1) both' }}
            >
                {children}
            </div>
        </div>
    );
};

export const CloseX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button type="button" onClick={onClick} aria-label="Close" className="hv-h-mint" style={{ width: 34, height: 34, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
);

/** Small rotating ring (the design's `hvSpin` spinner). */
export const Spinner: React.FC<{ size?: number; light?: boolean }> = ({ size = 17, light }) => (
    <span style={{ flex: 'none', width: size, height: size, borderRadius: 999, border: `2px solid ${light ? 'rgba(255,253,247,0.35)' : 'var(--hv-line-2)'}`, borderTopColor: light ? '#FFFDF7' : 'var(--hv-brand-ink)', animation: 'hvSpin 0.8s linear infinite', display: 'inline-block' }} />
);

/** Mono eyebrow label ("CHAT", "SUBDOMAIN"). */
export const Eyebrow: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.16em', color: 'var(--hv-muted)', ...style }}>{children}</span>
);

/** The four-point HyVay star used as the agent's avatar. */
export const Star: React.FC<{ size?: number }> = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="var(--hv-brand-ink)" aria-hidden="true"><path d="M12 0l2.4 9.6L24 12l-9.6 2.4L12 24l-2.4-9.6L0 12l9.6-2.4z" /></svg>
);

/**
 * Skeleton shown inside the preview frame while the first build has nothing to
 * render yet (booting the container, installing, or the agent writing its first
 * file). `label` is a small honest status line under the shapes.
 */
export const PreviewSkeleton: React.FC<{ label?: string; detail?: string }> = ({ label, detail }) => {
    const bar = (w: string | number, h: number, tone: 'line' | 'line-2' | 'mint' = 'line', radius: number | string = 999): React.CSSProperties => ({ width: w, height: h, borderRadius: radius, background: `var(--hv-${tone})`, display: 'block' });
    return (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--hv-card)' }}>
            <div style={{ padding: 26, display: 'flex', flexDirection: 'column', gap: 18, animation: 'hvSkel 1.8s ease-in-out infinite' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={bar(22, 22, 'line-2', 7)} />
                    <span style={bar(88, 9, 'line-2')} />
                    <span style={{ flex: 1 }} />
                    <span style={bar(52, 9)} />
                    <span style={bar(52, 9)} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '26px 0' }}>
                    <span style={bar(120, 11)} />
                    <span style={bar('64%', 30, 'line-2', 8)} />
                    <span style={bar('40%', 11)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                    {[0, 1, 2].map((i) => <div key={i} style={{ height: 110, borderRadius: 12, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }} />)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                    <span style={bar(150, 34, 'mint')} />
                    <span style={bar(180, 9)} />
                </div>
            </div>
            {(label || detail) && (
                <div style={{ marginTop: 'auto', padding: '0 26px 22px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
                    {label && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.16em', color: 'var(--hv-muted)' }}>
                            <Spinner size={12} /> {label}
                        </span>
                    )}
                    {detail && <span style={{ maxWidth: 420, fontFamily: FONT_MONO, fontSize: 10.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{detail}</span>}
                </div>
            )}
        </div>
    );
};

/** Centered "NOTHING HERE YET" placeholder from the Code/Media tabs. */
export const NothingHere: React.FC<{ icon: React.ReactNode; label?: string; hint?: string }> = ({ icon, label = 'NOTHING HERE YET', hint }) => (
    <div style={{ alignSelf: 'center', margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', padding: '44px 20px' }}>
        <span style={{ width: 54, height: 54, borderRadius: 999, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
        <Eyebrow>{label}</Eyebrow>
        {hint && <p style={{ margin: 0, maxWidth: 320, fontSize: 13, lineHeight: 1.6, color: 'var(--hv-body)' }}>{hint}</p>}
    </div>
);

/** The landing's dashed empty card ("No media yet"), reused for consistency. */
export const EmptyCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; action?: React.ReactNode }> = ({ icon, title, children, action }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '60px 28px', borderRadius: 16, border: '1.5px dashed var(--hv-line-2)', background: 'var(--hv-page-2)' }}>
        <span style={{ width: 52, height: 52, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 6 }}>{icon}</span>
        <div style={{ fontFamily: FONT_SERIF, fontSize: 26, letterSpacing: '-0.01em', color: 'var(--hv-ink)' }}>{title}</div>
        <p style={{ margin: 0, maxWidth: 340, textAlign: 'center', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-body)' }}>{children}</p>
        {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
);

/** Danger callout (publish shortfall, preview runtime error). */
export const DangerNote: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
    <div style={{ display: 'flex', gap: 11, padding: '14px 16px', borderRadius: 14, background: 'var(--hv-danger-bg)', color: 'var(--hv-danger)', ...style }}>
        <svg width="17" height="17" style={{ flex: 'none', marginTop: 1 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8.6" /><path d="M12 8v4.6" /><path d="M12 16h.01" /></svg>
        <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.6, fontWeight: 600 }}>{children}</div>
    </div>
);

/* -------------------------------------------------------------------- icons */

type IconProps = { size?: number; sw?: number };
const I = (size: number, sw: number, children: React.ReactNode) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

/** Outline icons traced from the design bundle (stroke = currentColor). */
export const Icons = {
    eye: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12S18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="3" /></>),
    code: ({ size = 14, sw = 2 }: IconProps) => I(size, sw, <path d="M9 7l-5 5 5 5M15 7l5 5-5 5" />),
    media: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="3" y="4.5" width="18" height="15" rx="2.5" /><path d="M3.6 16.5 8.5 11l4 4 3-2.6 4.4 4.1" /><circle cx="15.4" cy="9" r="1.4" /></>),
    desktop: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="2.5" y="4" width="19" height="12.5" rx="2" /><path d="M9 20h6" /></>),
    tablet: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="5" y="2.5" width="14" height="19" rx="2.4" /><path d="M11 18.5h2" /></>),
    phone: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="7" y="2.5" width="10" height="19" rx="2.4" /><path d="M11 18.8h2" /></>),
    undo: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M3.5 5.5v5h5" /><path d="M4.4 10.5A8.4 8.4 0 1 1 5 16.2" /></>),
    reload: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M20.5 5.5v5h-5" /><path d="M19.6 10.5A8.4 8.4 0 1 0 19 16.2" /></>),
    share: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><circle cx="18" cy="5.5" r="2.6" /><circle cx="6" cy="12" r="2.6" /><circle cx="18" cy="18.5" r="2.6" /><path d="M8.3 10.7 15.7 6.8M8.3 13.3l7.4 3.9" /></>),
    download: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <path d="M12 3.5v11M7.5 10.5 12 15l4.5-4.5M4 19.5h16" />),
    help: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><circle cx="12" cy="12" r="9" /><path d="M9.6 9.4a2.5 2.5 0 1 1 3.6 2.3c-.8.5-1.2 1-1.2 1.9" /><path d="M12 17h.01" /></>),
    moon: ({ size = 16, sw = 1.8 }: IconProps) => I(size, sw, <path d="M20 14.2A8.4 8.4 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" />),
    sun: ({ size = 16, sw = 1.8 }: IconProps) => I(size, sw, <><circle cx="12" cy="12" r="4.2" /><path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6M5.3 5.3l1.9 1.9M16.8 16.8l1.9 1.9M18.7 5.3l-1.9 1.9M7.2 16.8l-1.9 1.9" /></>),
    pencil: ({ size = 13, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M4 20h4l10-10a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6z" /><path d="M13.4 6.6 17 10.2" /></>),
    collapse: ({ size = 19, sw = 1.8 }: IconProps) => I(size, sw, <><rect x="3.5" y="4" width="17" height="16" rx="2.4" /><path d="M9.5 4v16M16 9.5 13 12l3 2.5" /></>),
    expand: ({ size = 19, sw = 1.8 }: IconProps) => I(size, sw, <><rect x="3.5" y="4" width="17" height="16" rx="2.4" /><path d="M9.5 4v16M13 9.5 16 12l-3 2.5" /></>),
    check: ({ size = 10, sw = 3.4 }: IconProps) => I(size, sw, <path d="M5 12.5l4.5 4.5L19 7" />),
    coins: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><circle cx="8" cy="9" r="5.3" /><circle cx="15" cy="15" r="5.3" /></>),
    up: ({ size = 15, sw = 2.4 }: IconProps) => I(size, sw, <path d="M12 19V5M6 11l6-6 6 6" />),
    arrow: ({ size = 15, sw = 2.4 }: IconProps) => I(size, sw, <path d="M5 12h13M13 6l6 6-6 6" />),
    external: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M14 4.5h5.5V10" /><path d="M19.5 4.5 11 13" /><path d="M19 14.5v3.5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3.5" /></>),
    fullscreen: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />),
    exitFullscreen: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <path d="M9 4v5H4M15 9V4h5M20 15h-5v5M4 15h5v5" />),
    globe: ({ size = 19, sw = 1.8 }: IconProps) => I(size, sw, <><circle cx="12" cy="12" r="8.6" /><path d="M3.4 12h17.2" /><path d="M12 3.4c2.2 2.4 3.4 5.4 3.4 8.6S14.2 18.2 12 20.6c-2.2-2.4-3.4-5.4-3.4-8.6S9.8 5.8 12 3.4z" /></>),
    copy: ({ size = 13, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="9" y="9" width="11" height="11" rx="2.2" /><path d="M15 5.6A2.2 2.2 0 0 0 12.8 4H6.2A2.2 2.2 0 0 0 4 6.2v6.6c0 1 .7 1.9 1.6 2.1" /></>),
    x: ({ size = 17, sw = 2.4 }: IconProps) => I(size, sw, <path d="M7 7l10 10M17 7L7 17" />),
    user: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><circle cx="12" cy="8" r="3.6" /><path d="M4.5 20c1.4-3.8 4.4-6 7.5-6s6.1 2.2 7.5 6" /></>),
    folder: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6h4l2 2.4h8A1.5 1.5 0 0 1 20 9.9v8.6A1.5 1.5 0 0 1 18.5 20h-14A1.5 1.5 0 0 1 3 18.5z" />),
    card: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><rect x="3" y="5.5" width="18" height="13" rx="2.2" /><path d="M3 10h18" /></>),
    logout: ({ size = 15, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M9 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H9" /><path d="M16 17l5-5-5-5M21 12H9" /></>),
    upload: ({ size = 20, sw = 1.8 }: IconProps) => I(size, sw, <path d="M12 16V5M7.5 9.5 12 5l4.5 4.5M4 19.5h16" />),
    trash: ({ size = 13, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M4 7h16M9.5 7V4.5h5V7M6.5 7l.8 12.2A1.5 1.5 0 0 0 8.8 20.6h6.4a1.5 1.5 0 0 0 1.5-1.4L17.5 7" /></>),
    plus: ({ size = 14, sw = 2.2 }: IconProps) => I(size, sw, <path d="M12 5v14M5 12h14" />),
    search: ({ size = 13, sw = 2 }: IconProps) => I(size, sw, <><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4-4" /></>),
    file: ({ size = 13, sw = 1.8 }: IconProps) => I(size, sw, <><path d="M6 3.5h7.5L19 9v11.5H6z" /><path d="M13.5 3.5V9H19" /></>),
    folderOpen: ({ size = 13, sw = 1.8 }: IconProps) => I(size, sw, <path d="M3.5 8.5A1.5 1.5 0 0 1 5 7h3.6l1.8 2.2H19a1.5 1.5 0 0 1 1.5 1.5v.8H6.4a1.5 1.5 0 0 0-1.4 1l-1.5 5z" />),
    folderClosed: ({ size = 13, sw = 1.8 }: IconProps) => I(size, sw, <path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h3.6l1.8 2.2H19a1.5 1.5 0 0 1 1.5 1.5v8.3A1.5 1.5 0 0 1 19 19.5H5a1.5 1.5 0 0 1-1.5-1.5z" />),
    chevronR: ({ size = 12, sw = 2.2 }: IconProps) => I(size, sw, <path d="M9 6l6 6-6 6" />),
    chevronD: ({ size = 12, sw = 2.2 }: IconProps) => I(size, sw, <path d="M6 9l6 6 6-6" />),
    terminal: ({ size = 13, sw = 1.9 }: IconProps) => I(size, sw, <><path d="M4 17l6-5-6-5" /><path d="M12 19h8" /></>),
    bolt: ({ size = 13, sw = 1.9 }: IconProps) => I(size, sw, <path d="M13 2.5 4.5 13.5H12l-1 8 8.5-11H12z" />),
    chart: ({ size = 14, sw = 1.9 }: IconProps) => I(size, sw, <path d="M4 20V11M10 20V5M16 20v-6M21 20H3" />),
};
